import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";

const vehicles = [
  {
    name: "Premium Sedan",
    make: "Genesis G90 or Cadillac XTS",
    price: "from $75 \u00b7 $90/hr (3-hour minimum)",
    capacity: "Up to 3 passengers",
    desc: "Premium black sedan service. Leather interior, complimentary water, phone chargers.",
    image: "/genesis.png",
  },
  {
    name: "Executive Sedan",
    make: "Mercedes-Benz S-Class",
    price: "from $95 \u00b7 $90/hr (3-hour minimum)",
    capacity: "Up to 3 passengers",
    desc: "Executive Mercedes-Benz black sedan. The pinnacle of comfort for corporate and VIP travel.",
    image: "/mercedes.png",
  },
  {
    name: "SUV",
    make: "Chevrolet Suburban or Tahoe",
    price: "from $100 \u00b7 $100/hr (3-hour minimum)",
    capacity: "Up to 6 passengers",
    desc: "Black SUV with extra cargo space. Ideal for airport groups, families, and weddings.",
    image: "/chevy_suburban.jpeg",
  },
  {
    name: "Business Executive SUV",
    make: "Cadillac Escalade",
    price: "from $120 \u00b7 $120/hr (3-hour minimum)",
    capacity: "Up to 6 passengers",
    desc: "Cadillac Escalade — the ultimate executive SUV. 3-hour minimum for hourly bookings.",
    image: "/cardilac_escalde.png",
  },
  {
    name: "Sprinter Van — Group Transport",
    make: "Mercedes-Benz Sprinter",
    price: "$175/hr (4-hour minimum)",
    capacity: "Up to 14 passengers",
    desc: "Black Mercedes-Benz Sprinter for group transportation, winery tours, sports events, and concerts.",
    image: "/mercedes_sprinter.png",
  },
];

function VehicleCard({ vehicle, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: index * 0.08 }}
      whileHover={{ y: -6, transition: { duration: 0.25 } }}
      className="group relative border border-white/5 hover:border-gold/40 overflow-hidden cursor-default transition-all duration-300 hover:shadow-xl hover:shadow-gold/5"
      style={{ background: "#1a1a1a" }}
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={vehicle.image}
          alt={vehicle.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-400 origin-left" />
        {/* Capacity badge */}
        <span className="absolute top-3 right-3 text-gold/80 text-xs tracking-wider uppercase border border-gold/20 px-2 py-1 backdrop-blur-sm"
          style={{ background: "rgba(0,0,0,0.6)" }}>
          {vehicle.capacity}
        </span>
      </div>

      <div className="p-6 flex flex-col gap-2">
        <h3 className="font-display text-cream text-lg font-medium">
          {vehicle.name}
        </h3>
        <p className="text-gold/70 text-xs tracking-wider uppercase">
          {vehicle.make}
        </p>
        <p className="text-cream/50 text-sm leading-relaxed mt-1">{vehicle.desc}</p>

        <div className="mt-auto pt-4 border-t border-white/5">
          <span className="text-gold text-sm font-semibold tracking-wide">
            {vehicle.price}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

export default function Services() {
  const headingRef = useRef(null);
  const headingInView = useInView(headingRef, { once: true, margin: "-60px" });

  return (
    <section
      id="services"
      className="py-24 md:py-32"
      style={{ background: "#0a0a0a" }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <motion.div
          ref={headingRef}
          initial={{ opacity: 0, y: 24 }}
          animate={headingInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          <p className="text-gold tracking-[0.3em] text-xs uppercase mb-4 font-medium">
            Our Fleet
          </p>
          <h2 className="section-heading">Choose Your Vehicle</h2>
          <div className="gold-divider" />
          <p className="text-cream/55 max-w-lg mx-auto mt-4 text-sm md:text-base leading-relaxed">
            All vehicles are black, professionally maintained, and driven by
            licensed chauffeurs.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {vehicles.map((v, i) => (
            <VehicleCard key={v.name} vehicle={v} index={i} />
          ))}
        </div>

        {/* Service types chips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={headingInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <p className="text-cream/40 text-xs tracking-widest uppercase mb-5">
            Available For
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              "Airport Transfers (STL)",
              "Corporate Travel",
              "Weddings & Events",
              "Winery Tours",
              "Graduation & Prom",
              "Long-Distance Trips",
            ].map((service) => (
              <span
                key={service}
                className="border border-gold/20 text-cream/70 text-sm px-4 py-2 tracking-wide"
                style={{ background: "rgba(201,168,76,0.04)" }}
              >
                {service}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Package pricing + info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={headingInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          {[
            { label: "Hourly Service", detail: "Contact us for pricing" },
            { label: "Airport Meet-and-Greet", detail: "Your chauffeur tracks your flight and meets you in baggage claim with a name sign. $20 fee." },
            { label: "Late-Night Surcharge", detail: "$20 applies to rides between 7 PM and 6 AM." },
          ].map((item) => (
            <div
              key={item.label}
              className="border border-white/5 p-5"
              style={{ background: "#1a1a1a" }}
            >
              <p className="text-gold text-sm font-semibold tracking-wide mb-1">{item.label}</p>
              <p className="text-cream/50 text-sm leading-relaxed">{item.detail}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
